import axios from 'axios';
import queryString from 'query-string';
import flatten from 'lodash-es/flatten';
import { transformAddonTitleToKey } from '@services/transform';

const USER_BASE = {
  house_keeper: 'housekeepers',
  property_manager: 'property-managers',
  admin: 'admins',
  super_admin: 'admins'
};

function createApi() {
  const apiClient = axios.create({
    baseURL: process.env.BACKEND_URL,
    responseType: 'json'
  });

  const setAuthHeader = authToken => {
    apiClient.defaults.headers.common['Authorization'] = `bearer ${authToken}`;
  };

  const checkUserEmailAvailability = async () => {
    return true;
  };

  const loginWithEmail = async ({ email, password }) => {
    const { data } = await apiClient.post('/auth/login', {
      email,
      password
    });
    return data;
  };

  const registerWithEmail = async ({
    firstName,
    lastName,
    phoneNumber,
    email,
    password,
    ...rest
  }) => {
    const { data } = await apiClient.post('/auth/signup', {
      firstName,
      lastName,
      phone: phoneNumber,
      email,
      password,
      ...rest
    });
    return data;
  };

  const getAuthToken = async ({ refreshToken, authToken }) => {
    setAuthHeader(authToken);
    const { data } = await apiClient.post('/auth/token', {
      refreshToken
    });
    return data;
  };

  const getImpersonate = async userId => {
    let URL = `/users/${userId}/impersonate`;

    const { data } = await apiClient.get(URL);
    return data;
  };

  const getSearchData = async () => {
    const { data } = await apiClient.get('/search/data');
    return data;
  };

  const getUserProfile = async () => {
    const { data } = await apiClient.get('/me');
    return data;
  };

  const fetchOffices = async () => {
    let URL = '/offices';
    const { data } = await apiClient.get(URL);
    return data;
  };

  /**
   * Clients
   * */
  const fetchClients = async params => {
    const { userID, userID1, userID2, propertyID, propertyID1, ...restParams } =
      params || {};
    const newParams = { ...restParams };

    const userIDParams = [];
    const propertyIDParams = [];

    if (userID) {
      userIDParams.push(userID);
    }

    if (userID1) {
      userIDParams.push(userID1);
    }

    if (userID2) {
      userIDParams.push(userID2);
    }

    if (userID2) {
      userIDParams.push(userID2);
    }

    if (propertyID) {
      propertyIDParams.push(propertyID);
    }

    if (propertyID1) {
      propertyIDParams.push(propertyID1);
    }

    if (userIDParams.length > 0) {
      newParams.userID = flatten(userIDParams);
    }

    if (propertyIDParams.length > 0) {
      newParams.propertyID = flatten(propertyIDParams);
    }

    let URL = '/members?' + queryString.stringify(newParams);
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchClient = async clientId => {
    let URL = `/members/${clientId}`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchClientKeyRelease = async clientId => {
    let URL = `/members/${clientId}/key-release`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  const saveInternalNote = async (clientId, email, internalNotes) => {
    let URL = `/members/${clientId}`;
    const { data } = await apiClient.patch(URL, { email, internalNotes });
    return data;
  };

  const fetchNextCleanNotes = async userID => {
    let URL =
      `/complaints/${userID}?` + queryString.stringify({ isPublished: false });
    const { data } = await apiClient.get(URL);
    return data;
  };

  const saveNextCleanNote = async (userID, complaint) => {
    // will need a note id if an old note exists
    let URL = '/complaints/';
    const { data } = await apiClient.post(URL, { userID, complaint });
    return data;
  };

  const setClientVIP = async (clientId, email, isVIP) => {
    let URL = `/members/${clientId}`;
    const { data } = await apiClient.patch(URL, { email, isVIP: !!isVIP });
    return data;
  };

  /**
   * Coupons
   * */

  const fetchCoupons = async () => {
    let URL = '/coupons';
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchCoupon = async couponID => {
    let URL = `/coupons/${couponID}`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  const createCoupon = async body => {
    const { data } = await apiClient.post('/coupons', body);
    return data;
  };

  const updateCoupon = async (couponId, body) => {
    const { data } = await apiClient.put(`/coupons/${couponId}`, body);
    return data;
  };

  /**
   * Properties
   */

  const fetchGlobalPropertySettings = async () => {
    let URL = '/properties/settings';
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchProperties = async params => {
    let URL = '/properties?' + queryString.stringify(params);
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchProperty = async propertyId => {
    let URL = `/properties/${propertyId}`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  const createPropertySettings = async (propertyId, values) => {
    let URL = `/properties/${propertyId}/settings`;
    const { data } = await apiClient.post(URL, values);
    return data;
  };

  const updatePropertySettings = async (
    propertyId,
    propertySettingsId,
    values
  ) => {
    let URL = `/properties/${propertyId}/settings/${propertySettingsId}`;
    const { data } = await apiClient.put(URL, values);
    return data;
  };

  const fetchPropertySettings = async propertyId => {
    let URL = `/properties/${propertyId}/settings`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  const activatePropertySetting = async (propertyId, propertySettingsId) => {
    let URL = `/properties/${propertyId}/settings/${propertySettingsId}/activate`;
    const { data } = await apiClient.post(URL);
    return data;
  };

  const createProperty = async values => {
    let URL = '/properties';
    const { data } = await apiClient.post(URL, values);
    return data;
  };

  const updateProperty = async (propertyId, values) => {
    let URL = `/properties/${propertyId}`;
    const { data } = await apiClient.put(URL, values);
    return data;
  };

  const updateHkPreferences = async (propertyId, values) => {
    let URL = `/properties/${propertyId}/housekeepers`;
    const { data } = await apiClient.put(URL, values);
    return data;
  };

  /**
   * Bookings
   */

  const markRequestComplete = async bookingId => {
    let URL = `/bookings/${bookingId}/complete`;
    const { data } = await apiClient.post(URL);
    return data;
  };

  const markRequestCanceled = async bookingId => {
    let URL = `/bookings/${bookingId}/cancel`;
    const { data } = await apiClient.post(URL);
    return data;
  };

  const fetchRequests = async params => {
    const { userID, userID1, additionalService, serviceDate, ...restParams } =
      params || {};
    const newParams = { ...restParams };

    if (serviceDate) {
      newParams.serviceDate = serviceDate.map(i => i.toISOString());
    }

    const userIDParams = [];

    if (userID) {
      userIDParams.push(userID);
    }

    if (userID1) {
      userIDParams.push(userID1);
    }

    if (userIDParams.length > 0) {
      newParams.userID = flatten(userIDParams);
    }

    if (additionalService && additionalService.length > 0) {
      newParams.additionalService = additionalService.map(
        transformAddonTitleToKey
      );
    }

    let URL = '/bookings/all?' + queryString.stringify(newParams);
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchRequest = async bookingId => {
    let URL = `/bookings/${bookingId}`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  /**
   * Users
   */

  const fetchUsers = async params => {
    const newParams = {
      ...params,
      limit: 10000
    };
    let URL = '/users?' + queryString.stringify(newParams);
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchUser = async userId => {
    let URL = `/users/${userId}`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchUserInfo = async (userId, role) => {
    let URL = `/${USER_BASE[role]}/info/${userId}`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  const createUser = async values => {
    const { role, ...restValues } = values;
    let URL = `/${USER_BASE[role]}`;
    const { data } = await apiClient.post(URL, restValues);
    return data;
  };

  const updateUser = async (userId, values) => {
    //this end point currently doesn't exist
    const { role, ...restValues } = values;
    let URL = `/${USER_BASE[role]}/${userId}`;
    const { data } = await apiClient.patch(URL, restValues);
    return data;
  };

  const updateActiveAt = async (userId, values) => {
    const { role, activeAt } = values;
    let URL = `/${USER_BASE[role]}/${userId}/activeAt`;
    const { data } = await apiClient.patch(URL, { activeAt });
    return data;
  };

  const activateHk = async (userId, role) => {
    let URL = `/${USER_BASE[role]}/${userId}/activate`;
    const { data } = await apiClient.post(URL);
    return data;
  };

  const deactivateHk = async (userId, role) => {
    let URL = `/${USER_BASE[role]}/${userId}/deactivate`;
    const { data } = await apiClient.post(URL);
    return data;
  };

  /**
   * Regions
   */

  const fetchRegions = async () => {
    let URL = '/regions/reports';
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchRegion = async regionId => {
    let URL = `/regions/reports/${regionId}`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  const fetchRegionHks = async regionId => {
    let URL = `/regions/${regionId}/housekeepers`;
    const { data } = await apiClient.get(URL);
    return data;
  };

  const updateHkAvailability = async (hkId, availabilities) => {
    let URL = `/housekeepers/availability/${hkId}`;
    const { data } = await apiClient.put(URL, { availabilities });
    return data;
  };

  return {
    setAuthHeader,
    loginWithEmail,
    registerWithEmail,
    checkUserEmailAvailability,
    getAuthToken,
    getImpersonate,
    getUserProfile,
    fetchClients,
    fetchClient,
    fetchClientKeyRelease,
    saveInternalNote,
    fetchNextCleanNotes,
    saveNextCleanNote,
    setClientVIP,
    fetchCoupons,
    fetchCoupon,
    createCoupon,
    updateCoupon,
    fetchGlobalPropertySettings,
    fetchProperties,
    createProperty,
    updateProperty,
    updateHkPreferences,
    createPropertySettings,
    updatePropertySettings,
    fetchPropertySettings,
    activatePropertySetting,
    fetchProperty,
    fetchRequests,
    fetchRequest,
    markRequestComplete,
    markRequestCanceled,
    fetchUsers,
    fetchUser,
    updateUser,
    createUser,
    fetchUserInfo,
    fetchOffices,
    updateActiveAt,
    activateHk,
    deactivateHk,
    getSearchData,
    fetchRegions,
    fetchRegion,
    fetchRegionHks,
    updateHkAvailability
  };
}

const ApiClient = createApi();

export default ApiClient;
